import React from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (_e) {
    return "";
  }
};

const fromLocalInput = (val) => {
  if (!val) return null;
  try {
    return new Date(val).toISOString();
  } catch (_e) {
    return null;
  }
};

/**
 * Two-state schedule picker:
 *   "asap"       — sends scheduled_at = null
 *   "scheduled"  — sends an ISO timestamp; client-side validates against
 *                  pre_execution_notice_minutes (too_soon) and 30-day cap.
 *
 * Uses the native datetime-local input — react-datepicker is installed but
 * doesn't play nicely with our MUI form styling. Native is sufficient here.
 */
const ScheduleField = ({
  mode,
  scheduledAt,
  onModeChange,
  onScheduledAtChange,
  noticeMinutes = 30,
  error,
}) => {
  const now = new Date();
  const minDt = new Date(now.getTime() + noticeMinutes * 60 * 1000);
  const maxDt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  return (
    <Box>
      <RadioGroup value={mode} onChange={(e) => onModeChange(e.target.value)}>
        <FormControlLabel
          value="asap"
          control={<Radio size="small" />}
          label="Run as soon as approved (ASAP)"
        />
        <FormControlLabel
          value="scheduled"
          control={<Radio size="small" />}
          label="Schedule for later"
        />
      </RadioGroup>

      {mode === "scheduled" && (
        <Box sx={{ mt: 1, ml: 4 }}>
          <TextField
            type="datetime-local"
            size="small"
            label="Run at"
            value={toLocalInput(scheduledAt)}
            onChange={(e) => onScheduledAtChange(fromLocalInput(e.target.value))}
            inputProps={{
              min: toLocalInput(minDt.toISOString()),
              max: toLocalInput(maxDt.toISOString()),
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 280 }}
            error={Boolean(error)}
            helperText={
              error ||
              `Pick a time between ${minDt.toLocaleString()} and ${maxDt.toLocaleString()}.`
            }
          />
        </Box>
      )}

      {mode === "scheduled" && !scheduledAt && (
        <Alert severity="info" sx={{ mt: 1, ml: 4 }}>
          Time must be at least <strong>{noticeMinutes} minutes</strong> from now and within{" "}
          <strong>30 days</strong>.
        </Alert>
      )}
    </Box>
  );
};

export default ScheduleField;
